import { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { OverlayPanel } from "primereact/overlaypanel";
import { Paginator } from "primereact/paginator";
import { Message } from "primereact/message";
import toast from "react-hot-toast";
import { useGetBoMeQuery } from "../../store/services/api";
import { useGetBoNotificationsQuery, useLazyDownloadBoNotificationFileQuery, useMarkBoNotificationReadMutation } from "../../store/services/boNotifications";
import { BoNotification } from "../../models/boNotification";
import { AppDialog } from "../../components/ui/AppDialog";
import { downloadBlob } from "../../utils/functions/downloadBlob";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { formatDateWithoutSeconds } from "../../utils/functions/date";
import "./boNotificationBell.css";

const RecipientNotificationBell = ({ userId }: { userId: number }) => {
  const panel = useRef<OverlayPanel>(null);
  const [page, setPage] = useState(1);
  const [visible, setVisible] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<BoNotification | null>(null);
  const { currentData: notifications, isFetching, isError, refetch } = useGetBoNotificationsQuery(
    { userId, page }, { pollingInterval: 15_000, skipPollingIfUnfocused: true }
  );
  const [markRead] = useMarkBoNotificationReadMutation();
  const [downloadFile, { isFetching: isDownloading }] = useLazyDownloadBoNotificationFileQuery({
    refetchOnFocus: false, refetchOnReconnect: false,
  });
  const unreadCount = notifications?.unreadCount ?? 0;

  const readNotification = async (notification: BoNotification) => {
    try {
      await markRead({ notificationId: notification.id, userId }).unwrap();
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "No se pudo marcar la notificación como leída");
    }
  };

  const downloadAttachment = async (notification: BoNotification) => {
    if (!notification.fileId) return;
    const request = downloadFile({ fileId: notification.fileId, userId });
    try {
      const file = await request.unwrap();
      downloadBlob(file.blob, file.filename);
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "No se pudo descargar el archivo");
    } finally {
      request.unsubscribe();
    }
  };

  const openNotification = (notification: BoNotification) => {
    setSelectedNotification(notification);
    panel.current?.hide();
    if (!notification.readAt) void readNotification(notification);
  };

  return (
    <>
      <div className="bo-notification-trigger">
        <Button
          type="button" icon="pi pi-bell" rounded text
          className="text-white" aria-label={unreadCount > 0 ? `Notificaciones: ${unreadCount} sin leer` : "Notificaciones"}
          aria-haspopup="dialog" aria-expanded={visible} aria-controls="bo-notifications-panel"
          onClick={(event) => {
            setVisible(!panel.current?.isVisible());
            panel.current?.toggle(event);
          }}
          tooltip={visible ? undefined : "Notificaciones"} tooltipOptions={{ position: "bottom" }}
        />
        {unreadCount > 0 && <Badge value={unreadCount > 99 ? "99+" : unreadCount} severity="danger" className="bo-notification-count" />}
      </div>
      <OverlayPanel
        ref={panel} id="bo-notifications-panel" className="bo-notification-panel"
        onShow={() => { setVisible(true); void refetch(); }} onHide={() => setVisible(false)}
      >
        <h2 className="text-lg font-semibold mt-0 mb-3">Notificaciones</h2>
        {isFetching && <span role="status" className="text-sm text-600">Actualizando…</span>}
        {isError && <Message severity="error" text="No se pudieron cargar las notificaciones." />}
        {!isFetching && !isError && notifications?.notifications.length === 0 && <p className="text-sm text-600">No hay notificaciones todavía.</p>}
        <ul className="bo-notification-list">
          {(notifications?.notifications ?? []).map((notification) => (
            <li key={notification.id} className={`bo-notification-entry ${notification.readAt ? "" : "bo-notification-entry--unread"}`}>
              <button
                type="button"
                className="bo-notification-preview"
                aria-haspopup="dialog"
                onClick={() => openNotification(notification)}
              >
                <span className="flex align-items-start justify-content-between gap-2">
                  <span className="text-base font-semibold text-left">{notification.title}</span>
                  {!notification.readAt && <span className="text-xs text-primary">Nueva</span>}
                </span>
                <time className="text-xs text-600 text-left" dateTime={notification.createdAt}>{formatDateWithoutSeconds(notification.createdAt)}</time>
              </button>
            </li>
          ))}
        </ul>
        {(notifications?.total ?? 0) > 20 && <Paginator first={(page - 1) * 20} rows={20} totalRecords={notifications?.total ?? 0} onPageChange={(event) => setPage(event.page + 1)} template="PrevPageLink CurrentPageReport NextPageLink" currentPageReportTemplate="{currentPage} de {totalPages}" />}
      </OverlayPanel>
      <AppDialog
        variant="detail"
        visible={selectedNotification !== null}
        onHide={() => setSelectedNotification(null)}
        className="bo-notification-dialog"
        header={
          <div className="bo-notification-detail-heading">
            <span className="bo-notification-detail-title">{selectedNotification?.title ?? "Notificación"}</span>
            {selectedNotification && (
              <time className="bo-notification-detail-date" dateTime={selectedNotification.createdAt}>
                <i className="pi pi-clock" aria-hidden="true" />
                {formatDateWithoutSeconds(selectedNotification.createdAt)}
              </time>
            )}
          </div>
        }
      >
        {selectedNotification && (
          <div className="bo-notification-detail">
            <p className="bo-notification-detail-message">{selectedNotification.message}</p>
            {selectedNotification.fileId && (
              <div className="bo-notification-detail-attachment">
                <Button type="button" label="Descargar archivo" icon="pi pi-download" size="small" outlined loading={isDownloading} onClick={() => { void downloadAttachment(selectedNotification); }} />
              </div>
            )}
          </div>
        )}
      </AppDialog>
    </>
  );
};

export const BoNotificationBell = () => {
  const { currentData: me } = useGetBoMeQuery();
  if (!me || (me.role !== "SUPER_ADMIN" && me.role !== "TENANT_ADMIN")) return null;
  return <RecipientNotificationBell key={me.userId} userId={me.userId} />;
};
